import React, { useEffect, useRef, useState } from "react";
import { loadGoogleMaps, MapPinPicker } from "./GoogleMap";
import { useLanguage } from "../../i18n";

export interface PropertyLocation {
  lat: number;
  lng: number;
  label: string;
}

// Picks the short "area, city" text out of a Geocoder/Places result's
// address components — e.g. "Indiranagar, Bengaluru" instead of the full
// formatted address, so it fits the existing Locality field.
export function extractLocalityText(components: { long_name: string; types: string[] }[]): string {
  const find = (type: string) => components.find((c) => c.types.includes(type))?.long_name;
  const area = find("sublocality_level_1") || find("sublocality") || find("neighborhood");
  const city = find("locality") || find("administrative_area_level_2");
  const parts = [...new Set([area, city].filter(Boolean))];
  return parts.join(", ");
}

export interface AddressParts {
  city: string;
  state: string;
  pincode: string;
}

// Pulls the structured city/state/pincode out of the same geocoding result
// the free-text locality already comes from — lets the admin console filter
// listings by region instead of having to parse one combined address string.
export function extractAddressParts(components: { long_name: string; short_name: string; types: string[] }[]): AddressParts {
  const find = (type: string) => components.find((c) => c.types.includes(type))?.long_name;
  return {
    city: find("locality") || find("administrative_area_level_2") || "",
    state: find("administrative_area_level_1") || "",
    pincode: find("postal_code") || "",
  };
}

// Shared by the GPS button and pin-drag handler below — both need to turn a
// bare lat/lng into a human-readable address once the owner has settled on
// a spot, rather than leaving them stuck looking at raw coordinates.
async function reverseGeocode(
  mapsKey: string,
  lat: number,
  lng: number
): Promise<{ label: string; localityText: string; addressParts: AddressParts }> {
  await loadGoogleMaps(mapsKey);
  const geocoder = new window.google.maps.Geocoder();
  const result: any = await new Promise((resolve, reject) => {
    geocoder.geocode({ location: { lat, lng } }, (results: any, status: string) => {
      if (status === "OK" && results?.[0]) resolve(results[0]);
      else reject(new Error(status));
    });
  });
  return {
    label: result.formatted_address,
    localityText: extractLocalityText(result.address_components ?? []),
    addressParts: extractAddressParts(result.address_components ?? []),
  };
}

export function LocationCaptureField({
  mapsKey,
  location,
  onChange,
  onLocalityDetected,
  onAddressDetected,
}: {
  mapsKey: string | null;
  location: PropertyLocation | null;
  onChange: (loc: PropertyLocation | null) => void;
  // `force` is true only when the pin itself was dragged/settled — that's
  // the owner explicitly correcting the location, so callers should
  // overwrite locality/city/state/pincode even if already filled in
  // (e.g. editing an existing property). Search/GPS pass force=false so
  // they don't clobber locality text the owner already typed by hand.
  onLocalityDetected?: (text: string, force: boolean) => void;
  onAddressDetected?: (parts: AddressParts, force: boolean) => void;
}) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [searchReady, setSearchReady] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Bumped only when the pin should jump to a brand-new spot (address
  // search, GPS) — remounts the picker so it recenters there. Panning the
  // map to fine-tune the pin never touches this, so it can't fight the user
  // mid-gesture by snapping back.
  const [resetKey, setResetKey] = useState(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!mapsKey || location) return;
    let cancelled = false;
    loadGoogleMaps(mapsKey)
      .then(() => {
        if (cancelled || !searchInputRef.current) return;
        const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
          fields: ["geometry", "formatted_address", "address_components", "name"],
        });
        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (!place?.geometry?.location) {
            setError(t("locationCapture.pickSuggestion"));
            return;
          }
          setError("");
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const label = place.formatted_address || place.name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
          const localityText = extractLocalityText(place.address_components ?? []);
          if (localityText) onLocalityDetected?.(localityText, false);
          onAddressDetected?.(extractAddressParts(place.address_components ?? []), false);
          onChange({ lat, lng, label });
          setResetKey((k) => k + 1);
        });
        setSearchReady(true);
      })
      .catch(() => {
        // search stays unavailable; GPS button still works independently
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsKey, location]);

  function capture() {
    if (!navigator.geolocation) {
      setError(t("locationCapture.locationNotSupported"));
      return;
    }
    setBusy(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        let label = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        if (mapsKey) {
          try {
            const geocoded = await reverseGeocode(mapsKey, lat, lng);
            label = geocoded.label;
            if (geocoded.localityText) onLocalityDetected?.(geocoded.localityText, false);
            onAddressDetected?.(geocoded.addressParts, false);
          } catch {
            // fall back to the raw coordinates label; locality stays whatever the owner typed
          }
        }
        onChange({ lat, lng, label });
        setResetKey((k) => k + 1);
        setBusy(false);
      },
      (err) => {
        setError(err.code === err.PERMISSION_DENIED ? t("locationCapture.locationPermissionDenied") : t("locationCapture.locationCouldNotGet"));
        setBusy(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  // The geocoded/GPS pin often lands a few meters off the actual building —
  // panning the map underneath the fixed pin lets the owner nudge it onto
  // the real spot even when they're adding a property from somewhere else
  // entirely, without retyping the address search.
  async function handleMapSettled(lat: number, lng: number) {
    if (!location) return;
    onChange({ lat, lng, label: location.label });
    if (!mapsKey) return;
    try {
      const geocoded = await reverseGeocode(mapsKey, lat, lng);
      onChange({ lat, lng, label: geocoded.label });
      if (geocoded.localityText) onLocalityDetected?.(geocoded.localityText, true);
      onAddressDetected?.(geocoded.addressParts, true);
    } catch {
      // keep the coordinate-derived position; label just won't refine
    }
  }

  return (
    <div className="field">
      <label>{t("locationCapture.pinLocationLabel")}</label>
      {location ? (
        <div className="card" style={{ padding: 12 }}>
          <div className="card-row">
            <div className="sub" style={{ maxWidth: 220 }}>
              📍 {location.label}
            </div>
            <button type="button" className="link-btn" onClick={() => onChange(null)}>
              {t("locationCapture.clear")}
            </button>
          </div>
          {mapsKey && (
            <div style={{ marginTop: 10 }}>
              <div style={{ position: "relative" }}>
                <MapPinPicker
                  key={resetKey}
                  apiKey={mapsKey}
                  center={{ lat: location.lat, lng: location.lng }}
                  onSettled={handleMapSettled}
                  height={200}
                />
                <button
                  type="button"
                  className="pin-picker-expand-btn"
                  onClick={() => setExpanded(true)}
                  aria-label={t("locationCapture.expandMapAria")}
                  title={t("locationCapture.expandMapAria")}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
                  </svg>
                </button>
              </div>
              <div className="field-hint" style={{ margin: "6px 0 0" }}>
                {t("locationCapture.panMapHint")}
              </div>
            </div>
          )}
          {expanded && mapsKey && (
            <div className="pin-picker-overlay">
              <div className="pin-picker-overlay-header">
                <button type="button" className="icon-btn" onClick={() => setExpanded(false)} aria-label={t("locationCapture.expandMapAria")}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <div className="sub">📍 {location.label}</div>
                <button type="button" className="btn-solid btn-sm" style={{ width: "auto", padding: "9px 16px" }} onClick={() => setExpanded(false)}>
                  {t("common.continue")}
                </button>
              </div>
              <div className="pin-picker-overlay-map">
                <MapPinPicker
                  key={`expanded-${resetKey}`}
                  apiKey={mapsKey}
                  center={{ lat: location.lat, lng: location.lng }}
                  onSettled={handleMapSettled}
                  height="100%"
                  rounded={false}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          {mapsKey && (
            <div className="input-wrap" style={{ marginBottom: 8 }}>
              <input
                ref={searchInputRef}
                placeholder={searchReady ? t("locationCapture.searchPlaceholder") : t("locationCapture.loadingSearch")}
                disabled={!searchReady}
              />
            </div>
          )}
          {mapsKey && <div className="field-hint" style={{ margin: "0 0 8px" }}>{t("locationCapture.or")}</div>}
          <button type="button" className="btn-outline btn-sm" disabled={busy} onClick={capture}>
            {busy ? t("locationCapture.locating") : t("locationCapture.useCurrentLocation")}
          </button>
          {error && (
            <div className="error-banner" style={{ marginTop: 8 }}>
              {error}
            </div>
          )}
        </>
      )}
      <div className="field-hint">
        {t("locationCapture.hint")}
      </div>
    </div>
  );
}
